import { Outlet, RouteObject } from 'react-router-dom';

import { lazyWithRetry } from '@/shared/lib/lazyWithRetry';

import { ROUTES } from '@/shared/constants';

import { VideoReadingGuard } from '@/entities/video-reading';

import { ActorProfileAccessRedirect } from '@/features/auth';

import { LoggedInUserComposition } from '../di/compositions/LoggedInUser';
import { sentryCreateBrowserRouter } from '../sentry';

const ActorMainPage = lazyWithRetry(() => import('@/pages/actor/main'));
const ActorProjectInfoPage = lazyWithRetry(
  () => import('@/pages/actor/project-info')
);
const ActorEventsPage = lazyWithRetry(() => import('@/pages/actor/events'));
const ActorCompaniesPage = lazyWithRetry(
  () => import('@/pages/actor/companies')
);
const ActorProfilePage = lazyWithRetry(() => import('@/pages/actor/profile'));
const ActorProfilePreviewPage = lazyWithRetry(
  () => import('@/pages/actor/profile-preview')
);
const ActorCastingInfoPage = lazyWithRetry(
  () => import('@/pages/actor/casting-info')
);
const ChooseExcerptPage = lazyWithRetry(
  () => import('@/pages/actor/choose-excerpt')
);
const RecordVideoReadingPage = lazyWithRetry(
  () => import('@/pages/actor/record-video-reading')
);
const PrompterSettingsPage = lazyWithRetry(
  () => import('@/pages/actor/prompter-settings')
);
const ActorFavoritesPage = lazyWithRetry(
  () => import('@/pages/actor/favorites')
);
const ActorSettingsPage = lazyWithRetry(() => import('@/pages/actor/settings'));
const ActorSupportPage = lazyWithRetry(() => import('@/pages/actor/support'));
const ActorResponsesPage = lazyWithRetry(
  () => import('@/pages/actor/responses')
);
const ActorCardsPage = lazyWithRetry(() => import('@/pages/actor/actor-cards'));

const ActorNotifications = lazyWithRetry(
  () => import('@/pages/actor/notifications')
);

const NotFoundPage = lazyWithRetry(() => import('@/pages/not-found'));
const EmailVerificationPage = lazyWithRetry(
  () => import('@/pages/email-verification')
);
const PhoneVerificationPage = lazyWithRetry(
  () => import('@/pages/phone-verification')
);
const PublicVideoPage = lazyWithRetry(() => import('@/pages/public-video'));

export const ACTOR_ROUTE_OBJECTS: RouteObject[] = [
  {
    path: ROUTES.public_video,
    element: <PublicVideoPage />
  },
  {
    element: (
      <LoggedInUserComposition>
        <Outlet />
      </LoggedInUserComposition>
    ),
    children: [
      {
        path: '/',
        element: <ActorMainPage />
      },
      {
        path: '/events',
        handle: {
          crumb: { name: 'Проекты' }
        },
        children: [
          {
            index: true,
            element: <ActorEventsPage />
          },
          {
            path: 'companies',
            element: <ActorCompaniesPage />
          },
          {
            path: 'companies/:companyId',
            element: <ActorEventsPage />
          },
          {
            path: 'projects',
            children: [
              {
                path: ':projectId',
                handle: {
                  crumb: { name: 'Проект' }
                },
                children: [
                  {
                    index: true,
                    element: <ActorProjectInfoPage />
                  },
                  {
                    path: 'castings/:castingId',
                    handle: {
                      crumb: { name: 'Кастинг' }
                    },
                    element: <ActorCastingInfoPage />
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: 'favorites',
        children: [
          {
            index: true,
            element: <ActorFavoritesPage />
          }
        ]
      },
      {
        path: 'settings',
        children: [
          {
            index: true,
            element: <ActorSettingsPage />
          }
        ]
      },
      {
        path: '/profile',
        handle: {
          crumb: {
            name: 'Моё портфолио'
          }
        },
        children: [
          {
            index: true,
            element: <ActorProfilePage />
          },
          {
            path: 'preview/:id',
            handle: { crumb: { name: 'Как меня видят кинокомпании' } },
            element: <ActorProfilePreviewPage />
          },
          {
            path: 'choose-excerpt',
            handle: {
              crumb: { name: 'Выбор отрывка для видеочитки' }
            },
            children: [
              {
                index: true,
                element: <ChooseExcerptPage />
              },
              {
                path: 'record-video-reading',
                handle: {
                  crumb: { name: 'Запись видео' }
                },
                children: [
                  {
                    index: true,
                    element: (
                      <VideoReadingGuard>
                        <RecordVideoReadingPage />
                      </VideoReadingGuard>
                    )
                  },
                  {
                    path: 'prompter-settings',
                    handle: {
                      crumb: { name: 'Настройки суфлёра' }
                    },
                    element: (
                      <VideoReadingGuard>
                        <PrompterSettingsPage />
                      </VideoReadingGuard>
                    )
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: 'notifications',
        children: [
          {
            index: true,
            element: <ActorNotifications />
          }
        ]
      },
      {
        path: 'support',
        children: [
          {
            index: true,
            element: <ActorSupportPage />
          }
        ]
      },
      {
        path: ROUTES.change_email,
        element: <EmailVerificationPage />
      },
      {
        path: ROUTES.change_phone,
        element: <PhoneVerificationPage />
      },
      {
        path: 'responses',
        children: [
          {
            index: true,
            element: <ActorResponsesPage />
          }
        ]
      },
      {
        path: 'actor-registry',
        children: [
          {
            index: true,
            element: <ActorCardsPage />
          }
        ]
      },
      {
        path: 'actors/:id',
        element: <ActorProfileAccessRedirect />
      },
      {
        path: '*',
        element: <NotFoundPage />
      }
    ]
  }
];

export const actorRouter = sentryCreateBrowserRouter(ACTOR_ROUTE_OBJECTS);
